import { CommonModule } from '@angular/common';
import { Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatLuxonDateModule, provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { DateTime } from 'luxon';
import { Subject, takeUntil } from 'rxjs';
import { Country, MY_DATE_FORMATS, Registration } from '../../../domain/registrator';
import { CountryService } from '../../../services/country.service';
import { EventService } from '../../../services/event.service';
import { MessageService } from '../../../services/message.service';

@Component({
  selector: 'app-registration-add',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatRadioModule,
    MatTableModule,
    MatCardModule,
    MatListModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatTabsModule,
    MatDatepickerModule,
    MatSortModule,
    MatDialogModule,
    MatLuxonDateModule
  ],
  providers: [
    provideLuxonDateAdapter(),
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_DATE_FORMATS,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB', // Or your desired locale
    },
  ],
  templateUrl: './registration-add.component.html',
  styleUrl: './registration-add.component.scss'
})
export class RegistrationAddComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly countryService = inject(CountryService);
  private readonly messageService = inject(MessageService);
  private readonly eventService = inject(EventService);
  private readonly dialogRef = inject(MatDialogRef<RegistrationAddComponent>)
  private readonly route = inject(ActivatedRoute);
  event = this.route.parent!.snapshot.data['event'];

  countries: Country[] = [];
  registrationForm!: FormGroup;

  private destroy$ = new Subject<void>(); // Used for automatic unsubscription

  constructor(@Inject(MAT_DIALOG_DATA) public data: { eventName: string, registration: Registration | null }) {
  }

  ngOnInit(): void {
    this.buildForm();
    this.countryService.countries.pipe(takeUntil(this.destroy$)).subscribe({
      next: (countries) => {
        this.countries = countries;
      },
      error: (error) => {
        console.error('Error loading countries:', error);
        this.messageService.showMessage('Failed to load countries list.', true);
      }
    });
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(): void {
    this.registrationForm = this.fb.group({
      date: [this.getCurrentDate(), Validators.required],
      organisation: ['', Validators.required],
      firstAttendee: this.getAdditionalAttendeeFormGroup(),
      additionalAttendees: this.fb.array([]),
    });
  }

  private getAdditionalAttendeeFormGroup(): FormGroup {
    return this.fb.group({
      jobTitle: [''],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', Validators.compose([Validators.required, Validators.email, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)])],
    });
  }

  get additionalAttendees() {
    return this.registrationForm.controls["additionalAttendees"] as FormArray;
  }

  addAttendee() {
    this.additionalAttendees.push(this.getAdditionalAttendeeFormGroup());
  }

  deleteAttendee(i: number) {
    this.additionalAttendees.removeAt(i);
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.registrationForm.valid) {
      const formValue: Registration = this.registrationForm.getRawValue();
      this.dialogRef.close(formValue);
    } else {
      this.markAllAsTouched(this.registrationForm);
    }
  }

  private markAllAsTouched(formGroup: FormGroup | FormArray): void {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAllAsTouched(control);
      } else {
        control.markAsTouched();
      }
    });
  }

  private getCurrentDate(): string {
    return DateTime.now().toISODate();
  }
}
